import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Coins } from 'lucide-react';

interface StablecoinData {
  id: number;
  ts: string | null;
  total_supply: number | null;
  usdt_supply: number | null;
  usdc_supply: number | null;
  dai_supply: number | null;
  usde_supply: number | null;
  usdt_dominance: number | null;
  usdc_dominance: number | null;
}

const STABLECOIN_COLORS = {
  USDT: '#26A17B',
  USDC: '#2775CA',
  DAI: '#F5AC37',
  USDe: '#7C3AED',
  Other: '#6B7280',
};

const formatSupply = (value: number | null): string => {
  if (!value) return '$0';
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  return `$${value.toLocaleString()}`;
};

const DonutChart = ({ usdt, usdc, other, size = 128 }: { usdt: number; usdc: number; other: number; size?: number }) => {
  const total = usdt + usdc + other;
  if (total === 0) return null;

  const usdtPercent = (usdt / total) * 100;
  const usdcPercent = (usdc / total) * 100;
  
  // SVG donut chart using stroke-dasharray
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  
  const usdtDash = (usdtPercent / 100) * circumference;
  const usdcDash = (usdcPercent / 100) * circumference;
  const otherDash = circumference - usdtDash - usdcDash;
  
  const usdtOffset = 0;
  const usdcOffset = -usdtDash;
  const otherOffset = -(usdtDash + usdcDash);

  const isCompact = size < 100;

  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        {/* USDT segment */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={STABLECOIN_COLORS.USDT}
          strokeWidth={isCompact ? 14 : 12}
          strokeDasharray={`${usdtDash} ${circumference - usdtDash}`}
          strokeDashoffset={usdtOffset}
        />
        {/* USDC segment */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={STABLECOIN_COLORS.USDC}
          strokeWidth={isCompact ? 14 : 12}
          strokeDasharray={`${usdcDash} ${circumference - usdcDash}`}
          strokeDashoffset={usdcOffset}
        />
        {/* Other segment */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={STABLECOIN_COLORS.Other}
          strokeWidth={isCompact ? 14 : 12}
          strokeDasharray={`${otherDash} ${circumference - otherDash}`}
          strokeDashoffset={otherOffset}
        />
      </svg>
      {!isCompact && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs text-muted-foreground font-medium">Dominance</span>
        </div>
      )}
    </div>
  );
};

const StablecoinRow = ({ 
  name, 
  supply, 
  dominance, 
  color 
}: { 
  name: string; 
  supply: number | null; 
  dominance?: number | null;
  color: string;
}) => (
  <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
    <div className="flex items-center gap-2">
      <div 
        className="w-3 h-3 rounded-full" 
        style={{ backgroundColor: color }}
      />
      <span className="font-medium text-sm">{name}</span>
    </div>
    <div className="text-right">
      <div className="font-mono text-sm">{formatSupply(supply)}</div>
      {dominance !== undefined && dominance !== null && (
        <div className="text-xs text-muted-foreground">{dominance.toFixed(1)}%</div>
      )}
    </div>
  </div>
);

interface StablecoinsCardProps {
  compact?: boolean;
}

export const StablecoinsCard = ({ compact = false }: StablecoinsCardProps) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['stablecoins-overview'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stablecoins_overview')
        .select('*')
        .order('ts', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as StablecoinData | null;
    },
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    staleTime: 4 * 60 * 1000, // 4 minutes
  });

  if (isLoading) {
    return (
      <Card className="glass-card">
        <CardHeader className={compact ? "pb-1 pt-3 px-4" : "pb-2"}>
          <CardTitle className={`flex items-center gap-2 ${compact ? "text-sm" : "text-lg"}`}>
            <Coins className={compact ? "h-4 w-4 text-primary" : "h-5 w-5 text-primary"} />
            Stablecoins
          </CardTitle>
        </CardHeader>
        <CardContent className={compact ? "px-4 pb-3" : ""}>
          <div className="animate-pulse flex flex-col items-center">
            <div className={`rounded-full bg-muted ${compact ? "w-16 h-16" : "w-32 h-32"}`} />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="glass-card">
        <CardHeader className={compact ? "pb-1 pt-3 px-4" : "pb-2"}>
          <CardTitle className={`flex items-center gap-2 ${compact ? "text-sm" : "text-lg"}`}>
            <Coins className={compact ? "h-4 w-4 text-primary" : "h-5 w-5 text-primary"} />
            Stablecoins
          </CardTitle>
        </CardHeader>
        <CardContent className={compact ? "px-4 pb-3" : ""}>
          <p className="text-sm text-muted-foreground text-center py-4">
            Dados indisponíveis
          </p>
        </CardContent>
      </Card>
    );
  }

  // Calculate "Other" as total minus known stablecoins
  const knownSupply = (data.usdt_supply || 0) + (data.usdc_supply || 0) + 
                      (data.dai_supply || 0) + (data.usde_supply || 0);
  const otherSupply = Math.max(0, (data.total_supply || 0) - knownSupply);
  
  // For donut: USDT, USDC, and Other (everything else)
  const usdtForDonut = data.usdt_supply || 0;
  const usdcForDonut = data.usdc_supply || 0;
  const otherForDonut = (data.total_supply || 0) - usdtForDonut - usdcForDonut;

  // Compact version for Overview dashboard
  if (compact) {
    return (
      <Card className="glass-card h-full">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Stablecoins</CardTitle>
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Coins className="h-4 w-4 text-primary" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <DonutChart 
              usdt={usdtForDonut} 
              usdc={usdcForDonut} 
              other={otherForDonut}
              size={72}
            />
            <div className="flex-1 space-y-1.5">
              <div className="text-lg font-bold">{formatSupply(data.total_supply)}</div>
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STABLECOIN_COLORS.USDT }} />
                  <span className="text-muted-foreground">USDT</span>
                  <span className="font-medium ml-auto">{data.usdt_dominance?.toFixed(0)}%</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STABLECOIN_COLORS.USDC }} />
                  <span className="text-muted-foreground">USDC</span>
                  <span className="font-medium ml-auto">{data.usdc_dominance?.toFixed(0)}%</span>
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Supply total de stablecoins</p>
        </CardContent>
      </Card>
    );
  }

  // Full version
  return (
    <Card className="glass-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Coins className="h-5 w-5 text-primary" />
            Stablecoins
          </CardTitle>
          <Badge variant="outline" className="font-mono">
            {formatSupply(data.total_supply)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <DonutChart 
          usdt={usdtForDonut} 
          usdc={usdcForDonut} 
          other={otherForDonut} 
        />
        
        <div className="flex justify-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STABLECOIN_COLORS.USDT }} />
            <span>USDT {data.usdt_dominance?.toFixed(0)}%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STABLECOIN_COLORS.USDC }} />
            <span>USDC {data.usdc_dominance?.toFixed(0)}%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STABLECOIN_COLORS.Other }} />
            <span>Other</span>
          </div>
        </div>

        <div className="pt-2">
          <StablecoinRow 
            name="USDT" 
            supply={data.usdt_supply} 
            dominance={data.usdt_dominance}
            color={STABLECOIN_COLORS.USDT} 
          />
          <StablecoinRow 
            name="USDC" 
            supply={data.usdc_supply} 
            dominance={data.usdc_dominance}
            color={STABLECOIN_COLORS.USDC} 
          />
          <StablecoinRow 
            name="DAI" 
            supply={data.dai_supply}
            color={STABLECOIN_COLORS.DAI} 
          />
          <StablecoinRow 
            name="USDe" 
            supply={data.usde_supply}
            color={STABLECOIN_COLORS.USDe} 
          />
        </div>
        
        {data.ts && (
          <p className="text-xs text-muted-foreground text-center pt-2">
            Atualizado: {new Date(data.ts).toLocaleString('pt-BR')}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
